// @ts-strict-ignore
import { Box } from "@saleor/macaw-ui-next";
import { Info } from "lucide-react";
import React from "react";
import { useIntl } from "react-intl";

import { analyticsMessages } from "../../messages";
import { EmptyState } from "../EmptyState";
import { surfaceCardStyle } from "./tokens";

export type InventoryRow = {
  sku: string;
  days: number;
};

interface InventoryTableProps {
  title: string;
  description?: string;
  info?: string;
  rows: InventoryRow[];
  columns: {
    sku: string;
    days: string;
  };
  onRowClick?: (row: InventoryRow) => void;
  headerActions?: React.ReactNode;
  loading?: boolean;
  na?: boolean;
  emptyMessage?: string;
  /** Threshold-based color: low days = good (green), high = warn/danger. */
  severityMode?: "high-is-bad" | "high-is-good";
  unit?: string;
}

const colorForDays = (days: number, mode: "high-is-bad" | "high-is-good") => {
  if (mode === "high-is-good") {
    if (days >= 90) return "#10B981";

    if (days >= 30) return "#F59E0B";

    return "#EF4444";
  }

  if (days >= 60) return "#EF4444";

  if (days >= 30) return "#F59E0B";

  return "#10B981";
};

export const InventoryTable: React.FC<InventoryTableProps> = ({
  title,
  description,
  info,
  rows,
  columns,
  onRowClick,
  headerActions,
  loading,
  na,
  emptyMessage,
  severityMode = "high-is-bad",
  unit,
}) => {
  const intl = useIntl();
  const empty = !na && !loading && (!rows || rows.length === 0);
  const maxDays = Math.max(1, ...(rows || []).map(r => r.days));

  return (
    <Box padding={5} style={{ ...surfaceCardStyle }}>
      <Box marginBottom={4} display="flex" justifyContent="space-between" alignItems="center" gap={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box as="div" fontSize={2} color="default2">
            {title}
          </Box>
          {info && (
            <Box
              as="span"
              display="inline-flex"
              alignItems="center"
              title={info}
              style={{ color: "#9CA3AF", cursor: "help" }}
            >
              <Info size={13} />
            </Box>
          )}
        </Box>
        {headerActions}
      </Box>
      {description && (
        <Box as="div" fontSize={1} color="default2" marginBottom={4} style={{ marginTop: -8, opacity: 0.8 }}>
          {description}
        </Box>
      )}

      {na ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonNA)}
        </Box>
      ) : loading ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonLoading)}
        </Box>
      ) : empty ? (
        <EmptyState message={emptyMessage ?? intl.formatMessage(analyticsMessages.commonNoData)} />
      ) : (
        <Box as="div" overflowX="auto">
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              fontSize: 13,
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                  }}
                >
                  {columns.sku}
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "8px 12px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                    width: 220,
                  }}
                >
                  {columns.days}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const color = colorForDays(r.days, severityMode);
                const pct = (r.days / maxDays) * 100;
                const displayDays = r.days >= 999 ? "∞" : r.days;

                return (
                  <tr
                    key={`${r.sku}-${i}`}
                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                    style={{
                      transition: "background 120ms ease",
                      cursor: onRowClick ? "pointer" : "default",
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLTableRowElement).style.background =
                        "rgba(15, 23, 42, 0.025)";
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLTableRowElement).style.background = "transparent";
                    }}
                  >
                    <td
                      style={{
                        padding: "10px 12px",
                        borderBottom:
                          i === rows.length - 1 ? "none" : "1px solid rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={3}>
                        <Box
                          as="span"
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          __width={20}
                          __height={20}
                          borderRadius={2}
                          style={{
                            background: "rgba(15, 23, 42, 0.06)",
                            color: "#374151",
                            fontSize: 11,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {i + 1}
                        </Box>
                        <Box
                          as="span"
                          style={{
                            color: "#111827",
                            fontWeight: 500,
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                            fontSize: 12,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.sku}
                        </Box>
                      </Box>
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        borderBottom:
                          i === rows.length - 1 ? "none" : "1px solid rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={3}>
                        <Box
                          __width={80}
                          __height={6}
                          borderRadius={1}
                          style={{
                            background: "rgba(15, 23, 42, 0.06)",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            __height={6}
                            style={{
                              width: `${Math.min(100, pct)}%`,
                              background: color,
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                        <Box
                          as="span"
                          style={{
                            fontWeight: 700,
                            color,
                            fontFeatureSettings: '"tnum"',
                            minWidth: 64,
                            textAlign: "right",
                          }}
                        >
                          {displayDays}
                          {unit ? ` ${unit}` : ""}
                        </Box>
                      </Box>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};
